import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { MovementId } from '../config/movements';
import type { RfidDocumentRow } from '../services/rfid.service';
import type { WizardVariant } from '../types/wizard';
import { zustandPersistStorage } from '../appState/zustandPersistStorage';

export type RecentDocumentScanStatus = 'scanning' | 'review' | 'posted';

export type RecentDocumentLineSnapshot = {
  itemNo: string;
  materialCode: string;
  materialDesc: string;
  quantity: number;
  scanQty: number;
  nonScanQty: number;
  scannable: boolean;
  fromBay: string;
  fromBin: string;
  rfidCodes: string[];
  kitLabel?: string;
};

/** Persisted SAP document the user opened and scanned tags against. */
export type RecentDocumentScan = {
  id: string;
  movementId: MovementId;
  movementTitle: string;
  variant: WizardVariant;
  document: RfidDocumentRow;
  scannedAt: number;
  updatedAt: number;
  status: RecentDocumentScanStatus;
  tagsScanned: number;
  linesComplete: number;
  linesInProgress: number;
  totalRequired: number;
  overallProgress: number;
  tagIds: string[];
  lines: RecentDocumentLineSnapshot[];
  sapMessage?: string;
  sapGeneratedDocument?: string;
  sapMaterialDocument?: string;
};

const MAX_DOCUMENTS_PER_USER = 40;
const STORAGE_KEY = 'rfid-recent-document-scans-v1';

/** Stable empty list — selectors must not return `[]` inline (infinite re-render). */
export const EMPTY_RECENT_DOCUMENT_SCANS: RecentDocumentScan[] = [];

type RecentScansState = {
  byUser: Record<string, RecentDocumentScan[]>;
  upsert: (userId: string, entry: RecentDocumentScan) => void;
  clearForUser: (userId: string) => void;
};

export function recentDocumentScanId(movementId: MovementId, documentNo: string): string {
  return `${movementId}:${documentNo.trim()}`;
}

export const useRecentScansStore = create<RecentScansState>()(
  persist(
    (set, get) => ({
      byUser: {},

      upsert: (userId, entry) => {
        set((s) => {
          const prev = s.byUser[userId] ?? [];
          const idx = prev.findIndex((e) => e.id === entry.id);
          let next: RecentDocumentScan[];
          if (idx >= 0) {
            const existing = prev[idx];
            const merged = { ...entry, scannedAt: existing.scannedAt };
            next = [merged, ...prev.filter((_, i) => i !== idx)];
          } else {
            next = [entry, ...prev].slice(0, MAX_DOCUMENTS_PER_USER);
          }
          return { byUser: { ...s.byUser, [userId]: next } };
        });
      },

      clearForUser: (userId) =>
        set((s) => {
          const byUser = { ...s.byUser };
          delete byUser[userId];
          return { byUser };
        }),
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => zustandPersistStorage),
      partialize: (s) => ({ byUser: s.byUser }),
    }
  )
);
